import * as React from 'react';
import {connect} from 'react-redux';

import {change} from 'redux-form';

import type {CoreEntityType, FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';

import {appHistory} from '../../../history';

import {MAX_CONTACTS_PER_LEAD} from '../../entity-form/footers/bulk-create-lead-footer';
import {
    LEADS_FORM_KEY,
    NUM_LEADS_CREATING,
    RELATED_ENTITY,
    RELATED_ENTITY_ACCOUNT_OPTION,
    RELATED_ENTITY_CONTACT_OPTION,
    MASS_SELECT_OPTIONS,
} from '../../entity-form/forms/lead-form-connected';

export type OwnProps = {
    isDisabled: boolean;
    filters: FilterObject[];
    q: string;
    entityData: {
        string: {
            id: string;
            primaryName: string;
        };
    };
    entityType: CoreEntityType;
    massSelect: boolean;
    selectedCount: number;
    allItems: any[];
    selectedItems: string[];
    omittedItems: string[];
};

type DispatchProps = {
    onCreateNewLead: (entityType: CoreEntityType, params: OnCreateNewLeadParams) => void;
};

type Props = OwnProps & DispatchProps;

type State = {
    action?: React.ReactNode;
    isEmailModalOpen: boolean;
    showMergeError: boolean;
    showLeadMergeModal: boolean;
    showConstantContactPopover: boolean;
};

type OnCreateNewLeadParams = {
    selectedItems: string[];
    omittedItems: string[];
    isMassSelect: boolean;
    filters: any[];
    q: string | null | undefined;
    selectedCount: number;
};

// @ts-expect-error
const mapDispatchToProps = (dispatch, ownProps: OwnProps): DispatchProps => ({
    onCreateNewLead: (entityType: CoreEntityType, params: OnCreateNewLeadParams) => {
        appHistory.push({
            hash: '/create/leads',
            state: {modal: true},
            search: window.location.search, // Preserve search/applied filters
        });

        // Make sure form is instantiated before trying to change its values
        setTimeout(() => {
            const selectedIds = determineSelectedIdsIfApplicable(params, ownProps.allItems);

            const peeps = selectedIds.map((entityId) => {
                // @ts-expect-error
                const entityData = ownProps.entityData[entityId];
                const entityName = entityData ? entityData.primaryName : undefined;

                return {
                    id: entityId,
                    type: ownProps.entityType,
                    value: entityName,
                    description: entityData.description,
                    tertiaryInfo: entityData.fields.address
                        ? entityData.fields.address.value
                        : null,
                    primaryEmailText: entityData.fields.email
                        ? entityData.fields.email.value
                        : null,
                    primaryPhoneText: entityData.fields.phone
                        ? entityData.fields.phone.value
                        : null,
                    avatarUrl: entityData.avatarUrl,
                };
            });

            // Populate default values (only needed if creating multiple)
            if (params.selectedCount > 1) {
                // Set the relatedEntity
                dispatch(change(LEADS_FORM_KEY, RELATED_ENTITY, entityType));
                dispatch(change(LEADS_FORM_KEY, NUM_LEADS_CREATING, params.selectedCount));

                if (params.isMassSelect) {
                    // Set the massSelect options
                    dispatch(
                        change(LEADS_FORM_KEY, MASS_SELECT_OPTIONS, {
                            omittedItems: params.omittedItems,
                            isMassSelect: params.isMassSelect,
                            filters: params.filters,
                            q: params.q,
                            selectedCount: params.selectedCount,
                        })
                    );
                }

                if (entityType === 'contacts') {
                    // Set the related entity account option to default single 'Company name' option
                    dispatch(
                        change(
                            LEADS_FORM_KEY,
                            'relatedEntityAccount',
                            RELATED_ENTITY_ACCOUNT_OPTION
                        )
                    );
                } else {
                    // Set the related entity contact option to default single 'Person name' option
                    dispatch(
                        change(
                            LEADS_FORM_KEY,
                            'relatedEntityContact',
                            RELATED_ENTITY_CONTACT_OPTION
                        )
                    );
                }
            }

            // Populate form with selected peeps
            // GREP: links.contacts, links.accounts
            dispatch(change(LEADS_FORM_KEY, `links.${entityType}`, peeps));
        }, 500);
    },
});

export class CreateLeadsButtonComponent extends React.Component<Props, State> {
    merge: HTMLElement | null | undefined;
    delete: HTMLElement | null | undefined;
    ccButton: HTMLElement | null | undefined;

    render() {
        return (
            <Button
                variant='secondary'
                size='big'
                disabled={this.props.isDisabled}
                onClick={this.handleCreateLeadClick}
            >
                Create lead
            </Button>
        );
    }

    handleCreateLeadClick = () => {
        this.props.onCreateNewLead(this.props.entityType, {
            // Array of entity ids selecteds
            selectedItems: this.props.selectedItems,
            // Integer of selected count, used for mass select
            selectedCount: this.props.selectedCount,
            // If mass selecting but unchecking a few items, we track ids here
            omittedItems: this.props.omittedItems,
            isMassSelect: this.props.massSelect,
            filters: this.props.filters,
            q: this.props.q,
        });
    };
}

// Given our lead creation params, determine the selectedIds.
function determineSelectedIdsIfApplicable(params: OnCreateNewLeadParams, allItems: string[]) {
    // If selectedCount is greater than 100, we don't need to do this.
    if (params.selectedCount > MAX_CONTACTS_PER_LEAD) return [];

    // If mass selecting, use our list of known entityIds and filter out omitted ones.
    if (params.isMassSelect) {
        return allItems.filter((entityId) => {
            return entityId && !params.omittedItems.includes(entityId);
        });
    }

    // Otherwise, just return the individually selected ids
    return params.selectedItems;
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(null, mapDispatchToProps);
export const CreateLeadsButton = connector(CreateLeadsButtonComponent);
